import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin, Package, TrendingUp, Clock, Shield, Zap, Eye, Users, DollarSign } from "lucide-react";
import Footer from "@/components/Footer";

// Logos
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function SupplierWelcomeLetterPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 p-1.5 flex items-center justify-center">
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-slate-900">AskIsaac</span>
          </Link>
          <div className="h-8 px-3 py-1 bg-slate-900 rounded-lg flex items-center justify-center">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-full w-auto object-contain" />
          </div>
        </div>
      </header>

      {/* Letter Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          
          {/* Letter Header */}
          <div className="bg-gradient-to-r from-purple-900 to-indigo-800 px-8 py-10 text-center">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-20 h-20 mx-auto mb-4 rounded-xl" />
            <h1 className="text-3xl font-bold text-white mb-2">Welcome to AskIsaac.io</h1>
            <p className="text-purple-300">On-Demand Supply Network — Supplier Partner Portal</p>
          </div>
          
          {/* Letter Body */}
          <div className="px-8 py-10 lg:px-12">
            <p className="text-slate-500 text-sm mb-8">January 2026</p>
            
            <p className="text-slate-800 text-lg leading-relaxed mb-6">
              Dear Valued Supplier Partner,
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              On behalf of the entire team at <strong>Sterile Mate LLC</strong>, I am delighted to welcome you to 
              <strong> AskIsaac.io</strong> — our on-demand healthcare operations platform that connects suppliers like you 
              directly to healthcare facilities when they need you most.
            </p>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">The On-Demand Advantage</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              Healthcare facilities are expanding their supplier networks to ensure reliable supply chains. 
              AskIsaac connects them to your inventory through our <strong>competitive bidding</strong> system — giving you access to new customers.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              Here's what makes it different: <strong>you bid without seeing competitors</strong>. No gaming the system, 
              no race-to-the-bottom pricing games. <strong>Best value wins</strong>. 
              Real competition that rewards suppliers who are ready to fulfill.
            </p>

            {/* Response Tiers */}
            <div className="bg-purple-50 rounded-xl p-6 mb-8 border border-purple-200">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                Response Tiers — Premium Margins for Faster Response
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-bold text-blue-800">Standard</p>
                  <p className="text-lg font-semibold text-slate-700">24-48 hrs</p>
                  <p className="text-xs text-slate-500">Routine orders</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-bold text-amber-800">Priority</p>
                  <p className="text-lg font-semibold text-slate-700">4-8 hrs</p>
                  <p className="text-xs text-slate-500">Urgent needs</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg border-2 border-red-300">
                  <p className="font-bold text-red-800">STAT</p>
                  <p className="text-lg font-semibold text-slate-700">2 hrs</p>
                  <p className="text-xs text-red-600 font-medium">Surgery-critical</p>
                </div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">What This Means for You</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              As an AskIsaac Supplier Partner, you gain access to:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                <Zap className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Instant Quote Requests</h3>
                <p className="text-slate-600 text-sm">Get notified the moment a hospital needs what you have. No more waiting for RFQs to trickle in.</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                <Eye className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Anonymous Bidding</h3>
                <p className="text-slate-600 text-sm">Bid without seeing competitors. Win on merit and availability, not on who bids lowest.</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                <Users className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Expanded Hospital Network</h3>
                <p className="text-slate-600 text-sm">Connect with healthcare facilities across the country when their primary suppliers fail them.</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                <DollarSign className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Fast Payment</h3>
                <p className="text-slate-600 text-sm">Streamlined invoicing through our platform ensures faster payment processing.</p>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">How It Works</h2>
            
            <ol className="list-decimal list-inside space-y-3 mb-8 text-slate-700">
              <li><strong>Get Notified</strong> — When a hospital posts a need in your category, you receive an instant alert via email and Telegram.</li>
              <li><strong>Review & Bid</strong> — See the requirements (item, quantity, urgency) and submit your quote. You can't see other bids.</li>
              <li><strong>Win & Fulfill</strong> — First qualified response wins. Ship the order and track delivery through the platform.</li>
              <li><strong>Get Paid</strong> — Submit your invoice for fast processing. No more chasing payments.</li>
            </ol>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Our Commitment to Partnership</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              We believe that strong supplier relationships are the backbone of reliable healthcare operations. AskIsaac isn't 
              just a tool — it's our commitment to making your business easier while helping hospitals get what they need, when 
              they need it.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              Your dedicated portal is ready at <a href="https://sterilemate.com" className="text-purple-600 font-semibold hover:underline">AskIsaac.io</a>. 
              If you haven't received your login credentials, please contact our team and we'll get you set up immediately.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-8">
              Thank you for being part of the AskIsaac network. Together, we're building a more efficient, transparent healthcare supply chain.
            </p>
            
            {/* Signature */}
            <div className="border-t border-slate-200 pt-8 mt-8">
              <p className="text-slate-800 font-semibold mb-1">With appreciation,</p>
              <p className="text-slate-900 text-xl font-bold mb-1">George Broughton III</p>
              <p className="text-slate-600 mb-6">President & CEO, Sterile Mate LLC</p>
              
              <div className="bg-slate-900 rounded-xl p-6 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-12 h-12 rounded-lg" />
                  <div>
                    <p className="font-bold text-purple-400">ASKISAAC</p>
                    <p className="text-slate-400 text-sm"></p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-300">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-400" />
                    <span>800.988.3892</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-400" />
                    <span>Direct: 281.456.3458</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-400" />
                    <span>Mobile: 800.988.3892</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-400" />
                    <span>george.broughton@sterilemate.com</span>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <MapPin className="w-4 h-4 text-purple-400" />
                    <span>396 W Greens Road, Suite 1139, Houston, TX 77067</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-8 text-center">
            <p className="text-white text-lg mb-4">Ready to start receiving quote requests?</p>
            <Link
              to="/supplier/login"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              Access Supplier Portal
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
